/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

interface DebugLog {
  timestamp: Date;
  level: 'log' | 'warn' | 'error' | 'info';
  message: string;
  data?: unknown;
  stack?: string;
}

interface ErrorDetails {
  message: string;
  stack?: string;
  componentStack?: string;
  timestamp: string;
  url?: string;
  userAgent?: string;
  additionalInfo?: Record<string, unknown>;
}

export interface DevDebugContextType {
  logs: DebugLog[];
  errors: ErrorDetails[];
  isDebugPanelOpen: boolean;
  openDebugPanel: () => void;
  closeDebugPanel: () => void;
  toggleDebugPanel: () => void;
  addLog: (level: DebugLog['level'], message: string, data?: unknown) => void;
  addError: (error: Error | ErrorDetails, additionalInfo?: Record<string, unknown>) => void;
  clearLogs: () => void;
  clearErrors: () => void;
}

export const DevDebugContext = createContext<DevDebugContextType | null>(null);

export type { DebugLog, ErrorDetails };

const MAX_LOGS = 200;
const MAX_ERRORS = 50;

export const DevDebugProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use ref for logs to avoid re-renders on every console.log
  // This fixes a crash caused by infinite render loops when useLingui + useDevDebug are used together
  const logsRef = useRef<DebugLog[]>([]);
  const [logsVersion, setLogsVersion] = useState(0); // Trigger re-render only when panel needs update
  const [errors, setErrors] = useState<ErrorDetails[]>([]);
  const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(false);

  const originalConsoleRef = useRef({
    log: console.log,
    warn: console.warn,
    error: console.error,
    info: console.info,
  });

  // Re-entry guard to prevent infinite recursion when console.warn/error triggers React warnings
  const isLoggingRef = useRef(false);

  const addLog = useCallback((level: DebugLog['level'], message: string, data?: unknown) => {
    const log: DebugLog = {
      timestamp: new Date(),
      level,
      message,
      data,
    };

    // Store in ref (no re-render) - fixes crash when useLingui + useDevDebug are used together
    logsRef.current = [...logsRef.current, log].slice(-MAX_LOGS);
  }, []);

  const addError = useCallback((error: Error | ErrorDetails, additionalInfo?: Record<string, unknown>) => {
    const errorDetails: ErrorDetails = error instanceof Error
      ? {
          message: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          userAgent: navigator.userAgent,
          additionalInfo,
        }
      : error;

    setErrors(prev => [...prev, errorDetails].slice(-MAX_ERRORS));
    
    // Also add to logs
    addLog('error', errorDetails.message, errorDetails);
  }, [addLog]);

  const openDebugPanel = useCallback(() => {
    setIsDebugPanelOpen(true);
    setLogsVersion(v => v + 1); // Refresh logs display when panel opens
  }, []);
  const closeDebugPanel = useCallback(() => setIsDebugPanelOpen(false), []);
  const toggleDebugPanel = useCallback(() => {
    setIsDebugPanelOpen(prev => {
      if (!prev) setLogsVersion(v => v + 1); // Refresh logs display when panel opens
      return !prev;
    });
  }, []);
  const clearLogs = useCallback(() => {
    logsRef.current = [];
    setLogsVersion(v => v + 1);
  }, []);
  const clearErrors = useCallback(() => setErrors([]), []);

  // Intercept console methods in dev mode
  useEffect(() => {
    if (!import.meta.env.DEV) return;

    // Copy ref to local variable to avoid stale closure issues
    const originalConsole = originalConsoleRef.current;

    const intercept = (level: DebugLog['level'], originalFn: (...args: unknown[]) => void) => {
      return (...args: unknown[]) => {
        // Call original console method first (always)
        originalFn.apply(console, args);

        // Guard against re-entry: when addLog triggers setLogs, React may call console.warn
        // about state updates outside act(), which would trigger this interceptor again
        if (isLoggingRef.current) return;
        isLoggingRef.current = true;

        try {
          // Capture to our logs
          const message = args.map(arg => {
            if (typeof arg === 'string') return arg;
            if (arg instanceof Error) return arg.message;
            try {
              return JSON.stringify(arg);
            } catch {
              return String(arg);
            }
          }).join(' ');

          addLog(level, message, args.length > 1 ? args : args[0]);
        } finally {
          isLoggingRef.current = false;
        }
      };
    };

    console.log = intercept('log', originalConsole.log);
    console.warn = intercept('warn', originalConsole.warn);
    console.error = intercept('error', originalConsole.error);
    console.info = intercept('info', originalConsole.info);

    return () => {
      // Restore original console methods
      console.log = originalConsole.log;
      console.warn = originalConsole.warn;
      console.error = originalConsole.error;
      console.info = originalConsole.info;
    };
  }, [addLog]);

  // Global error handler
  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const handleError = (event: ErrorEvent) => {
      addError(event.error || new Error(event.message), {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      addError(
        event.reason instanceof Error 
          ? event.reason 
          : new Error(`Unhandled Promise Rejection: ${String(event.reason)}`),
        { type: 'unhandledRejection' }
      );
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [addError]);

  // Keyboard shortcut: Ctrl/Cmd + Shift + D
  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'D') {
        event.preventDefault();
        toggleDebugPanel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleDebugPanel]);

  // Memoize context value to prevent unnecessary re-renders
  // logsVersion is used to trigger re-renders when logs need to be displayed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const logs = React.useMemo(() => logsRef.current, [logsVersion]);

  return (
    <DevDebugContext.Provider
      value={{
        logs,
        errors,
        isDebugPanelOpen,
        openDebugPanel,
        closeDebugPanel,
        toggleDebugPanel,
        addLog,
        addError,
        clearLogs,
        clearErrors,
      }}
    >
      {children}
    </DevDebugContext.Provider>
  );
};
